import { Box, Button, Typography } from '@mui/material'
import { createMedicine, deleteMedicine, getMedicine, updateMedicine } from 'api/medicine'
import { useGridControl } from 'hooks/useGridControl'
import { useRemoveModalControl } from 'hooks/useRemoveModalControl'
import { useShowControl } from 'hooks/useShowControl'
import moment, { Moment } from 'moment'
import React, { useCallback, useMemo, useState } from 'react'
import { useMutation } from 'react-query'
import { getActionColumn, RowAction } from 'features/UI'
import { Medicine } from 'types'
import { useGetDataSource } from 'utils'
import AddMedicineModal from '../../../modals/AddMedicineModal'
import ConfirmRemoveModal from '../../../modals/ConfirmRemoveModal'
import EditMedicineModal from '../../../modals/EditMedicineModal'
import NotificationSys from '../../../NotificationSystem'
import InfinityAgGrid from '../../../table/InfinityAgGrid'
import TableToolbar from '../../../table/TableToolbar'
import { medicinesColumnDefs } from '../const'

type Props = {
  carePersonId: number
  carePersonFullName: string
}

const formatMedicineDate = (value?: Date | string | Moment | null) => {
  if (!value) {
    return undefined
  }
  const parsed = moment(value)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined
}

const formatMedicinePayload = (
  medicine: Partial<Medicine> & { dates?: Date | string | Moment | null },
) => ({
  ...medicine,
  dates: formatMedicineDate(medicine.dates),
})

const ConcernProfileMedicinesTable = ({ carePersonId, carePersonFullName }: Props) => {
  const getMedicineDataSource = useGetDataSource(() => getMedicine(carePersonId))
  const { gridApi, onGridReady } = useGridControl(getMedicineDataSource)

  const [isOpen, handleOpen, handleClose] = useShowControl()
  const [isEditOpen, handleEditOpen, handleEditClose] = useShowControl()
  const [medicineToEdit, setMedicineToEdit] = useState<Medicine | null>(null)

  const createMedicineMutation = useMutation(createMedicine, {
    onSuccess: () => {
      handleClose()
      gridApi?.purgeInfiniteCache()
      NotificationSys.showSuccess(`Medicine added successfully`)
    },
  })

  const updateMedicineMutation = useMutation(updateMedicine, {
    onSuccess: () => {
      handleEditClose()
      setMedicineToEdit(null)
      gridApi?.purgeInfiniteCache()
      NotificationSys.showSuccess(`Medicine updated successfully`)
    },
  })

  const { idToRemove, openDeleteModal, closeDeleteModal, removeLoading, handleConfirmRemove } =
    useRemoveModalControl({
      deleteFunc: deleteMedicine, // TODO
      successCallback: () => gridApi?.purgeInfiniteCache(),
      warning: "Can't remove the medicine",
    })

  const handleAddMedicineSubmit = useCallback(
    (medicine: Partial<Medicine> & { dates?: Date | string | null }) => {
      createMedicineMutation.mutate({
        ...formatMedicinePayload(medicine),
        carePersonId: carePersonId,
      })
    },
    [createMedicineMutation, carePersonId],
  )

  const handleEditMedicineOpen = useCallback(
    (_id: number, props: { data?: Medicine }) => {
      if (!props.data) return
      setMedicineToEdit(props.data)
      handleEditOpen()
    },
    [handleEditOpen],
  )

  const handleEditMedicineClose = useCallback(() => {
    handleEditClose()
    setMedicineToEdit(null)
  }, [handleEditClose])

  const handleEditMedicineSubmit = useCallback(
    (medicine: Partial<Medicine> & { dates?: Date | string | null }) => {
      if (!medicineToEdit) return
      updateMedicineMutation.mutate({
        ...formatMedicinePayload(medicine),
        id: medicineToEdit.id,
      })
    },
    [updateMedicineMutation, medicineToEdit],
  )

  const actions: RowAction[] = useMemo(() => {
    return [
      { name: 'Edit', onClick: handleEditMedicineOpen },
      { name: 'Delete', onClick: openDeleteModal },
    ]
  }, [openDeleteModal, handleEditMedicineOpen])

  const columnDefsWithAction = [...medicinesColumnDefs, { ...getActionColumn(actions) }]

  return (
    <Box className="ag-theme-alpine">
      <Typography fontWeight={500} textAlign="center">
        Medicines for {carePersonFullName}
      </Typography>
      <Typography fontWeight={500} mb={2} textAlign="center">
        Include ALL prescription drugs over-the-counter drugs, vitamins, and herbal supplements):
      </Typography>

      <TableToolbar>
        <Button variant="outlined" sx={{ minWidth: 120 }} onClick={handleOpen}>
          Add Medicine
        </Button>
      </TableToolbar>
      <InfinityAgGrid
        columnDefs={columnDefsWithAction}
        onGridReady={onGridReady}
        domLayout="autoHeight"
      />
      <AddMedicineModal
        loading={createMedicineMutation.isLoading}
        isOpen={isOpen}
        handleClose={handleClose}
        onSubmit={handleAddMedicineSubmit}
      />
      <EditMedicineModal
        loading={updateMedicineMutation.isLoading}
        isOpen={isEditOpen}
        medicine={medicineToEdit}
        handleClose={handleEditMedicineClose}
        onSubmit={handleEditMedicineSubmit}
      />
      <ConfirmRemoveModal
        entityToRemove="Medicine"
        loading={removeLoading}
        isOpen={idToRemove !== null}
        handleConfirm={handleConfirmRemove}
        handleClose={closeDeleteModal}
      />
    </Box>
  )
}

export default ConcernProfileMedicinesTable
